import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from "@/services/core/service-registry";
import type {
  CreateRecurrencePatternRequest,
  EnhancedTodo,
  RecurrencePattern,
  UpdateRecurrencePatternRequest,
  ValidationResult,
} from "@/types/time-management";

/**
 * Interface for Recurrence Service - Phase 4 Architecture
 * Extends IService with comprehensive recurring task pattern operations
 */
export interface IRecurrenceService extends IService {
  // Core CRUD Operations
  createPattern(
    request: CreateRecurrencePatternRequest
  ): Promise<ServiceResponse<RecurrencePattern>>;

  getPattern(
    patternId: string
  ): Promise<ServiceResponse<RecurrencePattern | null>>;

  getUserPatterns(
    userId: string
  ): Promise<ServiceResponse<RecurrencePattern[]>>;

  updatePattern(
    patternId: string,
    updates: UpdateRecurrencePatternRequest
  ): Promise<ServiceResponse<RecurrencePattern>>;

  deletePattern(patternId: string): Promise<ServiceResponse<void>>;

  // Pattern Validation
  validatePattern(
    pattern: RecurrencePattern | CreateRecurrencePatternRequest
  ): Promise<ServiceResponse<ValidationResult>>;

  // Task Generation
  generateTasksFromPattern(
    patternId: string,
    taskTemplate: Partial<EnhancedTodo>,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<EnhancedTodo[]>>;

  generateNextOccurrence(
    pattern: RecurrencePattern,
    lastOccurrence?: string
  ): Promise<ServiceResponse<string | null>>;

  generateOccurrences(
    pattern: RecurrencePattern,
    startDate: string,
    endDate: string
  ): Promise<ServiceResponse<string[]>>;

  // Bulk Operations
  createRecurringTasks(
    patternId: string,
    taskTemplate: Partial<EnhancedTodo>,
    numberOfTasks: number
  ): Promise<ServiceResponse<EnhancedTodo[]>>;

  updateRecurringSeries(
    parentTaskId: string,
    updates: Partial<EnhancedTodo>,
    updateMode: "this_only" | "this_and_future" | "all"
  ): Promise<ServiceResponse<number>>;

  cancelRecurringSeries(
    parentTaskId: string,
    cancelMode: "this_only" | "this_and_future" | "all"
  ): Promise<ServiceResponse<number>>;

  // Pattern Analysis
  getPatternStats(patternId: string): Promise<
    ServiceResponse<{
      totalGeneratedTasks: number;
      completedTasks: number;
      upcomingTasks: number;
      averageCompletionTime: number;
      nextDueDate: string | null;
      patternHealth: "healthy" | "needs_attention" | "broken";
    }>
  >;

  analyzePatternEffectiveness(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      patterns: RecurrencePattern[];
      effectiveness: Record<string, number>;
      recommendations: string[];
    }>
  >;

  // Advanced Scheduling
  optimizePatternScheduling(
    patternId: string,
    constraints: {
      maxDailyTasks?: number;
      preferredTimeSlots?: { start: string; end: string }[];
      avoidWeekends?: boolean;
      businessHoursOnly?: boolean;
    }
  ): Promise<ServiceResponse<RecurrencePattern>>;

  detectPatternConflicts(
    patterns: RecurrencePattern[],
    dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      conflicts: {
        date: string;
        conflictingPatterns: string[];
        severity: "low" | "medium" | "high";
      }[];
      recommendations: string[];
    }>
  >;

  // Cleanup and Maintenance
  cleanupExpiredPatterns(): Promise<ServiceResponse<number>>;

  archiveCompletedRecurringSeries(
    beforeDate: string
  ): Promise<ServiceResponse<number>>;
}

/**
 * RecurrenceService - Phase 4 Architecture Implementation
 *
 * ✅ IService interface with initialize/dispose methods
 * ✅ ServiceResponse wrappers for all operations
 * ✅ typedSupabase client for enhanced type safety
 * ✅ Comprehensive error handling with service-specific error codes
 * ✅ Core service for recurring task ecosystem
 */
export class RecurrenceService implements IRecurrenceService {
  public readonly name = "RecurrenceService";
  private readonly supabase = typedSupabase;
  private initialized = false;

  // Service configuration
  private readonly DEFAULT_TIMEZONE = "Australia/Melbourne";
  private readonly MAX_GENERATED_TASKS = 1000;
  private readonly CLEANUP_BATCH_SIZE = 100;

  /**
   * Initialize the service - Phase 4 Architecture Compliant
   */
  async initialize(): Promise<void> {
    if (this.initialized) {return;}

    try {
      // Test database connection
      const { error } = await this.supabase
        .from("recurrence_patterns")
        .select("count")
        .limit(1);

      if (error) {
        throw new Error(`Database connection failed: ${error.message}`);
      }

      this.initialized = true;
      logger.info("✅ RecurrenceService initialized successfully");
    } catch (error) {
      logger.error("❌ RecurrenceService initialization failed:", { error: error });
      throw error;
    }
  }

  /**
   * Dispose the service - Phase 4 Architecture Compliant
   */
  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info("🧹 RecurrenceService disposed");
  }

  // ====================================================================================================
  // 🔄 CORE CRUD OPERATIONS
  // ====================================================================================================

  /**
   * Create a new recurrence pattern
   */
  async createPattern(
    request: CreateRecurrencePatternRequest
  ): Promise<ServiceResponse<RecurrencePattern>> {
    try {
      // Validate pattern before creation
      const validationResult = await this.validatePattern(request);
      if (validationResult.error) {
        return createErrorResponse(validationResult.error);
      }

      if (!validationResult.data?.isValid) {
        return createErrorResponse(validationResult.data?.warnings?.join(", ") ?? "Invalid pattern");
      }

      const { data, error } = await this.supabase
        .from("recurrence_patterns")
        .insert({
          pattern_type: request.pattern_type,
          interval: request.interval,
          days_of_week: request.days_of_week ?? null,
          day_of_month: request.day_of_month ?? null,
          end_date: request.end_date ?? null,
          max_occurrences: request.max_occurrences ?? null,
          skip_weekends: request.skip_weekends ?? false,
          timezone: request.timezone ?? this.DEFAULT_TIMEZONE,
        })
        .select()
        .single();

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(data as RecurrencePattern);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  /**
   * Get a recurrence pattern by ID
   */
  async getPattern(
    patternId: string
  ): Promise<ServiceResponse<RecurrencePattern | null>> {
    try {
      const { data, error } = await this.supabase
        .from("recurrence_patterns")
        .select("*")
        .eq("id", patternId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return createSuccessResponse(null);
        }
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(data as RecurrencePattern);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  /**
   * Get all recurrence patterns for a user
   */
  async getUserPatterns(
    userId: string
  ): Promise<ServiceResponse<RecurrencePattern[]>> {
    try {
      // Note: We need to link patterns to users through todos table
      // This query gets patterns used by the user's tasks
      const { data, error } = await this.supabase
        .from("recurrence_patterns")
        .select(
          `
          *,
          todos!inner(created_by)
        `
        )
        .eq("todos.created_by", userId);

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse((data || []) as RecurrencePattern[]);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  /**
   * Update a recurrence pattern
   */
  async updatePattern(
    patternId: string,
    updates: UpdateRecurrencePatternRequest
  ): Promise<ServiceResponse<RecurrencePattern>> {
    try {
      // Validate updates if provided
      if (Object.keys(updates).length > 0) {
        const { data: existingPattern, error: fetchError } =
          await this.getPattern(patternId);
        if (fetchError || !existingPattern) {
          return createErrorResponse(fetchError ?? "Pattern not found");
        }
        const validationResult = await this.validatePattern({
          ...existingPattern,
          ...updates,
        });
        if (validationResult.error) {
          return createErrorResponse(validationResult.error);
        }
      }

      const { data, error } = await this.supabase
        .from("recurrence_patterns")
        .update(updates)
        .eq("id", patternId)
        .select()
        .single();

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(data as RecurrencePattern);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  /**
   * Delete a recurrence pattern
   */
  async deletePattern(patternId: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("recurrence_patterns")
        .delete()
        .eq("id", patternId);

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(undefined);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  // ====================================================================================================
  // 🔍 PATTERN VALIDATION
  // ====================================================================================================

  /**
   * Validate a recurrence pattern
   */
  async validatePattern(
    pattern: RecurrencePattern | CreateRecurrencePatternRequest
  ): Promise<ServiceResponse<ValidationResult>> {
    try {
      const warnings: string[] = [];
      let isValid = true;

      // Validate interval
      if (pattern.interval <= 0) {
        warnings.push("Interval must be greater than 0");
        isValid = false;
      }

      // Validate pattern-specific requirements
      switch (pattern.pattern_type) {
        case "weekly":
          if (!pattern.days_of_week || pattern.days_of_week.length === 0) {
            warnings.push("Weekly patterns must specify days of week");
            isValid = false;
          }
          if (
            pattern.days_of_week &&
            pattern.days_of_week.some((day) => day < 0 || day > 6)
          ) {
            warnings.push(
              "Days of week must be between 0 (Sunday) and 6 (Saturday)"
            );
            isValid = false;
          }
          break;

        case "monthly":
          if (
            pattern.day_of_month &&
            (pattern.day_of_month < 1 || pattern.day_of_month > 31)
          ) {
            warnings.push("Day of month must be between 1 and 31");
            isValid = false;
          }
          break;

        case "daily":
          if (pattern.interval > 365) {
            warnings.push("Daily interval should not exceed 365 days");
          }
          break;

        case "yearly":
          if (pattern.interval > 10) {
            warnings.push("Yearly interval should not exceed 10 years");
          }
          break;
      }

      // Validate end conditions
      if (pattern.end_date && pattern.max_occurrences) {
        warnings.push(
          "Pattern should have either end_date OR max_occurrences, not both"
        );
      }

      if (!pattern.end_date && !pattern.max_occurrences) {
        warnings.push(
          "Pattern should have either end_date or max_occurrences for safety"
        );
      }

      if (pattern.end_date) {
        const endDate = new Date(pattern.end_date);
        const now = new Date();
        if (endDate <= now) {
          warnings.push("End date should be in the future");
          isValid = false;
        }
      }

      if (
        pattern.max_occurrences &&
        pattern.max_occurrences > this.MAX_GENERATED_TASKS
      ) {
        warnings.push(
          `Max occurrences should not exceed ${this.MAX_GENERATED_TASKS}`
        );
        isValid = false;
      }

      const result: ValidationResult = {
        isValid: isValid,
        errors: isValid ? [] : warnings,
        warnings,
      };

      return createSuccessResponse(result);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  // ====================================================================================================
  // 🎯 TASK GENERATION ENGINE
  // ====================================================================================================

  /**
   * Generate tasks from a recurrence pattern
   */
  async generateTasksFromPattern(
    patternId: string,
    taskTemplate: Partial<EnhancedTodo>,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<EnhancedTodo[]>> {
    try {
      // Get the pattern
      const patternResult = await this.getPattern(patternId);
      if (!patternResult.success || !patternResult.data) {
        return createErrorResponse(patternResult.success ? "Recurrence pattern not found" : patternResult.error);
      }

      const pattern = patternResult.data;

      // Generate occurrence dates
      const occurrencesResult = await this.generateOccurrences(
        pattern,
        dateRange.start,
        dateRange.end
      );

      if (!occurrencesResult.success || !occurrencesResult.data) {
        return {
          success: false,
          error: occurrencesResult.success ? "Failed to generate occurrences" : occurrencesResult.error,
        };
      }

      const occurrences = occurrencesResult.data;
      const generatedTasks: EnhancedTodo[] = [];

      // Create tasks for each occurrence
      for (const occurrence of occurrences) {
        const task: Partial<EnhancedTodo> = {
          ...taskTemplate,
          due_date: occurrence,
          recurrence_pattern_id: patternId,
          is_recurring: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Add unique identifier for recurring tasks
        task.title = `${
          taskTemplate.title ?? "Recurring Task"
        } - ${new Date(occurrence).toLocaleDateString()}`;

        generatedTasks.push(task as EnhancedTodo);
      }

      return createSuccessResponse(generatedTasks);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  /**
   * Generate the next occurrence date from a pattern
   */
  async generateNextOccurrence(
    pattern: RecurrencePattern,
    lastOccurrence?: string
  ): Promise<ServiceResponse<string | null>> {
    try {
      const startDate = lastOccurrence ? new Date(lastOccurrence) : new Date();
      const nextDate = new Date(startDate);

      switch (pattern.pattern_type) {
        case "daily": {
          nextDate.setDate(nextDate.getDate() + pattern.interval);
          break;
        }
        case "weekly": {
          if (!pattern.days_of_week || pattern.days_of_week.length === 0) {
            return createSuccessResponse(null);
          }

          // Find next occurrence day
          const currentDay = nextDate.getDay();
          logger.info("currentDay", { currentDay });
          const sortedDays = pattern.days_of_week.sort((a, b) => a - b);

          let nextDay = sortedDays.find((day) => day > currentDay);
          if (!nextDay) {
            // Next week
            nextDay = sortedDays[0];
            nextDate.setDate(nextDate.getDate() + (7 - currentDay + nextDay));
          } else {
            nextDate.setDate(nextDate.getDate() + (nextDay - currentDay));
          }
          break;
        }
        case "monthly": {
          nextDate.setMonth(nextDate.getMonth() + pattern.interval);
          if (pattern.day_of_month) {
            nextDate.setDate(pattern.day_of_month);
          }
          break;
        }
      }

      return createSuccessResponse(nextDate.toISOString());
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async generateOccurrences(
    pattern: RecurrencePattern,
    startDate: string,
    endDate: string
  ): Promise<ServiceResponse<string[]>> {
    try {
      const occurrences: string[] = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      let currentDate = new Date(start);
      let count = 0;

      // Safety limit to prevent infinite loops
      const maxIterations = this.MAX_GENERATED_TASKS;

      while (currentDate <= end && count < maxIterations) {
        // Check max occurrences limit
        if (
          pattern.max_occurrences &&
          occurrences.length >= pattern.max_occurrences
        ) {
          break;
        }

        // Check end date
        if (pattern.end_date && currentDate > new Date(pattern.end_date)) {
          break;
        }

        // Generate next occurrence
        const nextOccurrenceResult = await this.generateNextOccurrence(
          pattern,
          currentDate.toISOString()
        );

        if (nextOccurrenceResult.error || !nextOccurrenceResult.data) {
          break;
        }

        const nextOccurrence = nextOccurrenceResult.data;
        const nextDate = new Date(nextOccurrence);

        if (nextDate > end) {
          break;
        }

        occurrences.push(nextOccurrence);
        currentDate = nextDate;
        count++;
      }

      return createSuccessResponse(occurrences);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  createRecurringTasks(
    _patternId: string,
    _taskTemplate: Partial<EnhancedTodo>,
    _numberOfTasks: number
  ): Promise<ServiceResponse<EnhancedTodo[]>> {
    throw new Error("Method not implemented.");
  }
  updateRecurringSeries(
    _parentTaskId: string,
    _updates: Partial<EnhancedTodo>,
    _updateMode: "this_only" | "this_and_future" | "all"
  ): Promise<ServiceResponse<number>> {
    throw new Error("Method not implemented.");
  }
  cancelRecurringSeries(
    _parentTaskId: string,
    _cancelMode: "this_only" | "this_and_future" | "all"
  ): Promise<ServiceResponse<number>> {
    throw new Error("Method not implemented.");
  }
  analyzePatternEffectiveness(
    _userId: string,
    _dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      patterns: RecurrencePattern[];
      effectiveness: Record<string, number>;
      recommendations: string[];
    }>
  > {
    throw new Error("Method not implemented.");
  }
  optimizePatternScheduling(
    _patternId: string,
    _constraints: {
      maxDailyTasks?: number;
      preferredTimeSlots?: { start: string; end: string }[];
      avoidWeekends?: boolean;
      businessHoursOnly?: boolean;
    }
  ): Promise<ServiceResponse<RecurrencePattern>> {
    throw new Error("Method not implemented.");
  }
  detectPatternConflicts(
    _patterns: RecurrencePattern[],
    _dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      conflicts: {
        date: string;
        conflictingPatterns: string[];
        severity: "low" | "medium" | "high";
      }[];
      recommendations: string[];
    }>
  > {
    throw new Error("Method not implemented.");
  }
  archiveCompletedRecurringSeries(
    _beforeDate: string
  ): Promise<ServiceResponse<number>> {
    throw new Error("Method not implemented.");
  }

  // ====================================================================================================
  // 📊 PATTERN ANALYSIS AND OPTIMIZATION
  // ====================================================================================================

  /**
   * Get statistics for a recurrence pattern
   */
  async getPatternStats(patternId: string): Promise<
    ServiceResponse<{
      totalGeneratedTasks: number;
      completedTasks: number;
      upcomingTasks: number;
      averageCompletionTime: number;
      nextDueDate: string | null;
      patternHealth: "healthy" | "needs_attention" | "broken";
    }>
  > {
    try {
      // Get all tasks generated from this pattern
      const { data: tasks, error } = await this.supabase
        .from("todos")
        .select("*")
        .eq("recurrence_pattern_id", patternId);

      if (error) {
        return createErrorResponse(error.message);
      }

      if (!tasks || tasks.length === 0) {
        return createSuccessResponse({
          totalGeneratedTasks: 0,
          completedTasks: 0,
          upcomingTasks: 0,
          averageCompletionTime: 0,
          nextDueDate: null,
          patternHealth: "healthy" as const,
        });
      }

      // Calculate stats
      const totalGeneratedTasks = tasks.length;
      const completedTasks = tasks.filter((t) => t.status === "completed").length;
      const upcomingTasks = tasks.filter(
        (t) => t.status !== "completed" && t.due_date
      ).length;

      // Calculate average completion time
      const completedWithDates = tasks.filter(
        (t) => t.status === "completed" && t.actual_end && t.created_at
      );
      const averageCompletionTime =
        completedWithDates.length > 0
          ? completedWithDates.reduce((sum, t) => {
              const created = new Date(t.created_at).getTime();
              const completed = new Date(t.actual_end!).getTime();
              return sum + (completed - created);
            }, 0) / completedWithDates.length / (1000 * 60 * 60) // Convert to hours
          : 0;

      // Find next due date
      const upcomingTasksWithDates = tasks
        .filter(
          (task) =>
            task.status !== "completed" &&
            task.due_date &&
            new Date(task.due_date) > new Date()
        )
        .sort(
          (a, b) =>
            new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime()
        );

      const nextDueDate =
        upcomingTasksWithDates.length > 0
          ? upcomingTasksWithDates[0].due_date
          : null;

      // Determine pattern health
      const completionRate = totalGeneratedTasks > 0 ? completedTasks / totalGeneratedTasks : 0;
      let patternHealth: "healthy" | "needs_attention" | "broken" = "healthy";

      if (completionRate < 0.5) {
        patternHealth = "broken";
      } else if (completionRate < 0.7) {
        patternHealth = "needs_attention";
      }

      return createSuccessResponse({
        totalGeneratedTasks,
        completedTasks,
        upcomingTasks,
        averageCompletionTime,
        nextDueDate,
        patternHealth,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  /**
   * Clean up expired recurrence patterns
   */
  async cleanupExpiredPatterns(): Promise<ServiceResponse<number>> {
    try {
      const now = new Date().toISOString();

      const { data, error } = await this.supabase
        .from("recurrence_patterns")
        .delete()
        .or(`end_date.lt.${now},max_occurrences.eq.0`)
        .select("id");

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(data?.length || 0);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  // ====================================================================================================
  // 🔧 PRIVATE HELPER METHODS
  // ====================================================================================================

  /**
   * Generate suggestions for pattern optimization
   */
  private generatePatternSuggestions(
    pattern: RecurrencePattern | CreateRecurrencePatternRequest
  ): string[] {
    const suggestions: string[] = [];

    // Suggest improvements based on pattern type
    if (pattern.pattern_type === "daily" && pattern.interval === 1) {
      suggestions.push(
        "Consider using a weekly pattern with selected days instead of daily"
      );
    }

    if (pattern.pattern_type === "weekly" && !pattern.skip_weekends) {
      suggestions.push(
        "Consider enabling 'skip weekends' for work-related tasks"
      );
    }

    if (!pattern.end_date && !pattern.max_occurrences) {
      suggestions.push(
        "Add an end date or maximum occurrences to prevent indefinite task generation"
      );
    }

    if (pattern.max_occurrences && pattern.max_occurrences > 100) {
      suggestions.push(
        "Consider using an end date instead of a high occurrence count"
      );
    }

    return suggestions;
  }
}

// Export singleton instance
export const recurrenceService = new RecurrenceService();
